import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useQuery } from '@apollo/client';
import { GET_SEATING_DATA } from 'store/queries';
import { Spinner } from 'admin-components/components';
import { legShape, seatsShape, passengerShape, nextAvailableTrainShape } from 'constants/propTypes';
import config from 'utils/config';
import { FormattedMessage } from 'react-intl';
import { Row, Column } from 'components/shared/Layout';
import SeatMapSvg from './SeatMapSvg';
import { SEAT_PRIORITY_CODE } from 'constants/seating';
import useSeatsByTicketsQuery from './Hooks/useSeatsByTicketsQuery';
import { getUnavailableSeatsData } from '../SeatSelection/SeatSelection.utils';
import { StyledSeatMapWrapper } from '../SeatSelection/SeatSelection.styles';
import UnavailableSeatPill from './UnavailableSeatPill';

const proxyUrl = config('PROXY_URL') || '';

const getTemplateName = (carriage, seats) => {
  if (seats && seats.length > 0) {
    if (carriage) {
      const array = carriage.template.split('/');

      return array[array.length - 1];
    }

    return undefined;
  }

  return undefined;
};

const getCarriage = (data, currentCoach) => data.getSeatingData.carriages.find((c) => c.carriageNumber === currentCoach.toString());

const SeatMap = ({
  className,
  journey: {
    id,
    trainNumber,
    destination,
    origin,
    timing: { date }
  },
  currentCoach,
  currentInventoryClasses = [],
  seats,
  originalSeats,
  onSelectNewSeat,
  onSelectNewSeats,
  onSelectNewPassenger,
  onRemovePassengerSelection,
  selectedPassenger,
  selectedPassengers,
  nextAvailableTrain,
  isERTrain = false,
  showUnavailableSeatsReason = false,
  reference,
  onShowUnavailableSeatReason,
  showUnavailable,
  seatStatus,
  sendSeatsWithBooking,
  sendSeatsWithoutBooking
}) => {
  const [unavailableSeatsWithoutBookings, setUnavailableSeatsWithoutBookings] = useState([]);
  const [unavailableSeatsWithBookings, setUnavailableSeatsWithBookings] = useState([]);
  const [unavailableSeatsWithBookingsForCoach, setUnavailableSeatsWithBookingsForCoach] = useState([]);
  const handleFetchTicketsResponse = (items) => {
    setUnavailableSeatsWithBookings(items);
    const unavailableForCoach = items?.filter(
      (item) => reference !== item.reference && currentCoach === item.coach && currentInventoryClasses.includes(item.class)
    );
    setUnavailableSeatsWithBookingsForCoach(unavailableForCoach);
    sendSeatsWithBooking({ unavailableSeatsWithBookingsForCoach: unavailableForCoach });
    return;
  };

  const handleStates = ({ withBookings = [], withoutBookings = [] }) => {
    setUnavailableSeatsWithBookingsForCoach(withBookings);
    setUnavailableSeatsWithoutBookings(withoutBookings);
    sendSeatsWithBooking({ unavailableSeatsWithBookingsForCoach: withBookings });
    sendSeatsWithoutBooking({ unavailableSeatsWithoutBookings: withoutBookings });
    return;
  };

  const [fetchBySeats, { loading: seatsLoading }] = useSeatsByTicketsQuery({ onCompleted: handleFetchTicketsResponse });

  const { loading, error, data } = useQuery(GET_SEATING_DATA, {
    variables: {
      serviceId: nextAvailableTrain?.trainNumber || trainNumber,
      destination: destination.uic,
      origin: origin.uic,
      travelDate: date
    },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    onCompleted: (data) => {
      onHandleBookingWithSeats(data);
    }
  });

  const onHandleBookingWithSeats = (seatingData) => {
    if (showUnavailableSeatsReason) {
      const { differentInventory } = getUnavailableSeatsData({
        seatingData,
        currentCoach,
        currentInventoryClasses,
        currentSeats: seats,
        getCarriage
      });

      fetchBySeats({
        variables: {
          trainNumber: nextAvailableTrain?.trainNumber || trainNumber,
          departureDate: date,
          originUIC: origin.uic,
          destinationUIC: destination.uic
        }
      });

      setUnavailableSeatsWithoutBookings(differentInventory);
      sendSeatsWithoutBooking({ unavailableSeatsWithoutBookings: differentInventory });
    }
  };

  useEffect(
    () => {
      if (showUnavailableSeatsReason && data?.getSeatingData) {
        const { differentInventory, otherBookingsSeatsAndCoaches } = getUnavailableSeatsData({
          seatingData: data,
          currentCoach,
          currentInventoryClasses,
          currentSeats: seats,
          getCarriage
        });

        if (otherBookingsSeatsAndCoaches.length) {
          const unavailableForCoach = unavailableSeatsWithBookings.filter(
            (item) => reference !== item.reference && currentCoach === item.coach && currentInventoryClasses.includes(item.class)
          );
          handleStates({ withBookings: unavailableForCoach, withoutBookings: differentInventory });
          return;
        } else {
          handleStates({ withoutBookings: differentInventory });
          return;
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentCoach]
  );

  if (loading || seatsLoading) {
    return (
      <Row span={16}>
        <Column justifyContent="center" alignItems="center">
          <Spinner />
        </Column>
      </Row>
    );
  }

  if (error || !data) {
    return <SeatMapErrorMarkUp />;
  }

  const carriage = getCarriage(data, currentCoach);

  if (!carriage) {
    return <SeatMapErrorMarkUp />;
  }

  const templateName = getTemplateName(carriage, seats);
  const availableSeats =
    currentInventoryClasses.length > 0
      ? carriage.seats
          .filter(
            (seat) =>
              !!seat.inventoryClass &&
              seat.available &&
              !seat.blocked &&
              !seat.allocated &&
              currentInventoryClasses.includes(seat.inventoryClass)
          )
          .map((seat) => ({
            ...seat,
            coach: currentCoach
          }))
      : carriage.seats
          .filter((seat) => !!seat.inventoryClass && seat.available && !seat.blocked && !seat.allocated)
          .map((seat) => ({
            ...seat,
            coach: currentCoach
          }));

  const prioritySeats = carriage.seats.filter((seat) => seat.propertyCodes.includes(SEAT_PRIORITY_CODE));

  if (!templateName) {
    return <SeatMapErrorMarkUp />;
  }

  return (
    <StyledSeatMapWrapper>
      <UnavailableSeatPill show={showUnavailable} seatStatus={seatStatus} />
      <SeatMapSvg
        {...{
          seats,
          originalSeats,
          className,
          templateName,
          availableSeats,
          prioritySeats,
          currentCoach,
          onSelectNewSeat: onSelectNewSeat(id),
          onSelectNewSeats: onSelectNewSeats(id),
          onSelectNewPassenger,
          selectedPassenger,
          selectedPassengers,
          onRemovePassengerSelection,
          onShowUnavailableSeatReason,
          isERTrain,
          seatsData: [...unavailableSeatsWithBookingsForCoach, ...unavailableSeatsWithoutBookings]
        }}
        url={`${proxyUrl}/seat-map/?template=${encodeURIComponent(templateName)}&x-api-key=${config('API_GATEWAY_API_KEY')}`}
      />
    </StyledSeatMapWrapper>
  );
};

const SeatMapErrorMarkUp = () => (
  <>
    <FormattedMessage id="seatmap.svgError" />
    <button type="submit" onClick={() => window.location.reload()}>
      Try again
    </button>
  </>
);

SeatMap.propTypes = {
  journey: legShape.isRequired,
  className: PropTypes.string,
  currentCoach: PropTypes.string,
  seats: seatsShape.isRequired,
  originalSeats: seatsShape.isRequired,
  selectedPassenger: passengerShape,
  selectedPassengers: PropTypes.arrayOf(passengerShape),
  onSelectNewSeat: PropTypes.func.isRequired,
  onSelectNewSeats: PropTypes.func.isRequired,
  onSelectNewPassenger: PropTypes.func.isRequired,
  onRemovePassengerSelection: PropTypes.func.isRequired,
  currentInventoryClasses: PropTypes.arrayOf(PropTypes.string),
  nextAvailableTrain: nextAvailableTrainShape,
  isERTrain: PropTypes.bool,
  showUnavailableSeatsReason: PropTypes.bool,
  reference: PropTypes.string,
  onShowUnavailableSeatReason: PropTypes.func,
  showUnavailable: PropTypes.bool,
  seatStatus: PropTypes.shape({
    reference: PropTypes.string,
    seat: PropTypes.string,
    inventoryClass: PropTypes.string,
    blocked: PropTypes.bool
  }),
  sendSeatsWithBooking: PropTypes.func,
  sendSeatsWithoutBooking: PropTypes.func
};

export default SeatMap;
